'use client';

import React from 'react';
import { useFormContext } from 'react-hook-form';
import FormSection from './FormSection';
import FormField from './FormField';
import TextInput from './TextInput';
import RadioOption from './RadioOption';
import type { RSVPFormData } from './RSVPPageClient';

const travelOptions = [
  { value: 'alone', label: "I\'m travelling alone" },
  { value: 'spouse', label: 'With my spouse / partner' },
  { value: 'family', label: 'With family' },
  { value: 'friends', label: 'With friends' },
  { value: 'other', label: 'Other' },
];

export default function Section1PersonalInfo() {
  const {
    register,
    formState: { errors },
  } = useFormContext<RSVPFormData>();

  return (
    <FormSection
      number={1}
      title="Tell Us About You"
      description="Help us know who is joining us and where you'll be travelling from."
      animate={false}
    >
      <FormField
        label="Your Full Name"
        required
        htmlFor="fullName"
        error={errors.fullName?.message}
      >
        <TextInput
          id="fullName"
          type="text"
          placeholder="e.g. Priya Sharma"
          hasError={!!errors.fullName}
          registerProps={register('fullName', {
            required: 'Please enter your full name',
            minLength: {
              value: 2,
              message: 'Name must be at least 2 characters',
            },
          })}
        />
      </FormField>

      <FormField
        label="WhatsApp / Contact Number"
        required
        htmlFor="phone"
        error={errors.phone?.message}
      >
        <TextInput
          id="phone"
          type="tel"
          placeholder="e.g. +91 98765 43210"
          hasError={!!errors.phone}
          registerProps={register('phone', {
            required: 'Please enter your contact number',
            pattern: {
              value: /^[+\d\s\-()]{7,20}$/,
              message: 'Please enter a valid phone number',
            },
          })}
        />
      </FormField>

      <FormField
        label="Email Address"
        htmlFor="email"
        error={errors.email?.message}
        helper="Optional — we'll use this only to send you event updates"
      >
        <TextInput
          id="email"
          type="email"
          placeholder="e.g. priya@gmail.com"
          hasError={!!errors.email}
          registerProps={register('email', {
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: 'Please enter a valid email address',
            },
          })}
        />
      </FormField>

      <FormField
        label="Where will you be travelling from?"
        required
        htmlFor="origin"
        error={errors.origin?.message}
      >
        <TextInput
          id="origin"
          type="text"
          placeholder="City, State — e.g. Mumbai, Maharashtra"
          hasError={!!errors.origin}
          registerProps={register('origin', {
            required: 'Please tell us where you are travelling from',
          })}
        />
      </FormField>

      <FormField label="Who are you travelling with?">
        <div className="grid gap-2 mt-1">
          {travelOptions.map((opt) => (
            <RadioOption
              key={`travel-with-${opt.value}`}
              name="travellingWith"
              value={opt.value}
              label={opt.label}
              registerProps={register('travellingWith')}
            />
          ))}
        </div>
      </FormField>

      <FormField
        label="Total number of people travelling with you"
        required
        htmlFor="guestCount"
        helper="Please include yourself in the total count"
        error={errors.guestCount?.message}
      >
        <TextInput
          id="guestCount"
          type="number"
          min={1}
          max={50}
          hasError={!!errors.guestCount}
          registerProps={register('guestCount', {
            required: 'Please enter total number of guests',
            min: { value: 1, message: 'Minimum 1 person' },
            max: { value: 50, message: 'Please contact us directly for groups over 50' },
            valueAsNumber: true,
          })}
        />
      </FormField>
    </FormSection>
  );
}